import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useViewport } from '../../hooks/useViewport';
import { ProfileImage } from '../ui/ProfileImage';
import { useCompanyBranding } from '../../hooks/unified';
import type { HeaderProps } from '../../types/navigation';
import {
  ADMIN_WORKSPACE_PERMISSIONS,
  defaultLanding,
  hasAnyPermission,
  PERMISSIONS,
} from '../../utils/permissions';

export function Header({ companyInfo, navigationItems }: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const { user, isAuthenticated, isAdmin, role, permissions, logout } = useAuth();
  const location = useLocation();
  const { isMobile, prefersReducedMotion } = useViewport();
  
  // Get dynamic branding from system settings
  const branding = useCompanyBranding();
  const accountHome = defaultLanding(role, permissions);
  const accountHomeLabel = role === 'panelist' ? 'Panel Workspace' : isAdmin ? 'Hiring Workspace' : 'Dashboard';
  const roleLabel = role === 'owner' ? 'Super Admin' : role === 'hr_lead' ? 'HR Admin' : role === 'recruiter' ? 'HR Member' : role === 'panelist' ? 'Panelist' : null;
  
  // Default logo from public folder
  const defaultLogo = '/logo192.png';
  
  // Merge with props, preferring system settings, with default logo fallback
  const effectiveCompanyInfo = {
    name: branding.name || companyInfo.name,
    tagline: branding.tagline || companyInfo.tagline,
    logo: branding.logo || companyInfo.logo || defaultLogo,
  };

  // Filter navigation items based on authentication state
  const visibleNavItems = navigationItems.filter(item => {
    if (item.adminOnly && !hasAnyPermission(permissions, ADMIN_WORKSPACE_PERMISSIONS)) return false;
    if (item.requiresAuth && !isAuthenticated) return false;
    return true;
  });
  if (role === 'panelist' && hasAnyPermission(permissions, [PERMISSIONS.assignedInterviewsView])) {
    visibleNavItems.push({ label: 'Panel', href: '/panel', requiresAuth: true });
  }

  // Scroll-based header visibility (hide on scroll down, show on scroll up)
  const handleScroll = useCallback(() => {
    if (!isMobile) {
      setIsHeaderVisible(true);
      return;
    }

    const currentScrollY = window.scrollY;
    const scrollThreshold = 10;

    if (Math.abs(currentScrollY - lastScrollY) < scrollThreshold) {
      return;
    }

    if (currentScrollY > lastScrollY && currentScrollY > 100) {
      // Scrolling down & past threshold - hide header
      setIsHeaderVisible(false);
    } else {
      // Scrolling up - show header
      setIsHeaderVisible(true);
    }

    setLastScrollY(currentScrollY);
  }, [isMobile, lastScrollY]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsUserMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      setIsUserMenuOpen(false);
    } catch {
      // Handle logout error silently
    }
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleUserMenu = () => {
    setIsUserMenuOpen(!isUserMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActiveRoute = (href: string) => {
    return location.pathname === href;
  };

  // Animation classes based on reduced motion preference
  const transitionClasses = prefersReducedMotion 
    ? '' 
    : 'transition-all duration-300';

  return (
    <header 
      className={`bg-white shadow-md border-b border-gray-200 sticky top-0 z-50 ${transitionClasses} ${
        isHeaderVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
    >
      <div className="max-w-7xl mx-auto px-2 sm:px-6 lg:px-8 safe-area-inset-x">
        <div className="flex justify-between items-center h-14 sm:h-16">
          {/* Logo and Company Name - full height on mobile with left padding */}
          <div className="flex items-center h-full py-1 sm:py-2 pl-2 sm:pl-0">
            <Link to="/" className="flex items-center h-full sm:space-x-3">
              {effectiveCompanyInfo.logo ? (
                <img
                  src={effectiveCompanyInfo.logo}
                  alt={`${effectiveCompanyInfo.name} logo`}
                  className="h-full max-h-10 sm:max-h-12 w-auto max-w-[160px] sm:max-w-[240px] object-contain"
                />
              ) : (
                <div className="h-9 w-9 sm:h-10 sm:w-10 bg-primary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg sm:text-xl">
                    {effectiveCompanyInfo.name.charAt(0)}
                  </span>
                </div>
              )}
              <div className="hidden sm:block ml-3">
                <h1 className="text-xl font-bold text-gray-900">{effectiveCompanyInfo.name}</h1>
                {effectiveCompanyInfo.tagline && (
                  <p className="text-xs text-gray-500">{effectiveCompanyInfo.tagline}</p>
                )}
              </div>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-8">
            {visibleNavItems.map((item) => (
              <Link
                key={item.href}
                to={item.href}
                className={`px-3 py-2 rounded-md text-sm font-medium transition-colors duration-200 ${
                  isActiveRoute(item.href)
                    ? 'text-primary-600 bg-primary-50 border-b-2 border-primary-600'
                    : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>

          {/* Desktop User Menu */}
          <div className="hidden md:flex items-center space-x-4">
            {isAuthenticated ? (
              <div className="relative">
                <button
                  onClick={toggleUserMenu}
                  className="flex items-center space-x-2 p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                  aria-expanded={isUserMenuOpen}
                  aria-haspopup="true"
                >
                  <ProfileImage
                    src={user?.avatar}
                    alt={user?.name || user?.email || 'User'}
                    name={user?.name}
                    email={user?.email}
                    userId={user?.id}
                    size="sm"
                  />
                  <span className="text-sm font-medium text-gray-700">
                    {user?.name || user?.email}
                  </span>
                  <svg
                    className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                      isUserMenuOpen ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* User Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-xl shadow-xl py-2 z-50 border border-gray-100 backdrop-blur-sm">
                    <div className="px-4 py-3 border-b border-gray-100 bg-gray-50 rounded-t-xl">
                      <p className="text-sm font-medium text-gray-900">{user?.name || 'User'}</p>
                      <p className="text-xs text-gray-500">{user?.email}</p>
                      {roleLabel && (
                        <span className="inline-block mt-2 px-2 py-1 text-xs bg-primary-100 text-primary-800 rounded-full">
                          {roleLabel}
                        </span>
                      )}
                    </div>
                    <Link
                      to={accountHome}
                      className="block mx-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-all duration-200"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      {accountHomeLabel}
                    </Link>
                    {accountHome !== '/dashboard' && (
                      <Link
                        to="/dashboard"
                        className="block mx-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-all duration-200"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        My Career Dashboard
                      </Link>
                    )}
                    {!role && (
                      <Link
                        to="/dashboard/profile"
                        className="block mx-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 hover:text-gray-900 rounded-lg transition-all duration-200"
                        onClick={() => setIsUserMenuOpen(false)}
                      >
                        Profile Settings
                      </Link>
                    )}
                    <div className="border-t border-gray-100 mt-2 pt-2">
                      <button
                        onClick={handleLogout}
                        className="block w-full text-left mx-2 px-3 py-2 text-sm text-error hover:bg-error/10 rounded-lg transition-all duration-200"
                      >
                        Sign Out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <Link
                to="/login"
                className="bg-primary-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-primary-700 transition-colors duration-200"
              >
                Sign In
              </Link>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden">
            <button
              onClick={toggleMobileMenu}
              className="touch-target p-2 rounded-md text-gray-700 hover:text-primary-600 hover:bg-gray-100 transition-colors duration-200"
              aria-expanded={isMobileMenuOpen}
              aria-label="Toggle mobile menu"
            >
              <svg
                className="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d={isMobileMenuOpen 
                    ? "M6 18L18 6M6 6l12 12" 
                    : "M4 6h16M4 12h16M4 18h16"
                  }
                  className={prefersReducedMotion ? '' : 'transition-all duration-200'}
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Slide-out */}
      <div 
        className={`md:hidden fixed inset-0 z-50 ${isMobileMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
        aria-hidden={!isMobileMenuOpen}
      >
        {/* Backdrop */}
        <div
          className={`fixed inset-0 bg-black/60 backdrop-blur-sm ${transitionClasses} ${
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={closeMobileMenu}
        />
        
        {/* Slide-out Panel - solid white background */}
        <div 
          className={`fixed inset-y-0 left-0 w-full max-w-xs shadow-2xl bg-white ${transitionClasses} ${
            isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
            <div className="flex items-center space-x-3">
              {effectiveCompanyInfo.logo ? (
                <img
                  src={effectiveCompanyInfo.logo}
                  alt={`${effectiveCompanyInfo.name} logo`}
                  className="h-8 w-auto"
                />
              ) : (
                <div className="h-8 w-8 bg-primary-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">
                    {effectiveCompanyInfo.name.charAt(0)}
                  </span>
                </div>
              )}
              <h2 className="text-lg font-semibold text-gray-900">{effectiveCompanyInfo.name}</h2>
            </div>
            <button
              onClick={closeMobileMenu}
              className="touch-target p-2 rounded-md text-gray-400 hover:text-gray-600 bg-white"
              aria-label="Close menu"
            >
              <svg className="h-6 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content - scrollable */}
          <div className="h-[calc(100vh-64px)] overflow-y-auto bg-white">
            {/* Main Navigation */}
            <div className="px-4 py-4 border-b border-gray-100 bg-white">
              <p className="px-2 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Menu</p>
              <nav className="space-y-1">
                {visibleNavItems.map((item) => (
                  <Link
                    key={item.href}
                    to={item.href}
                    onClick={closeMobileMenu}
                    className={`block px-4 py-3 rounded-lg text-base font-medium ${
                      isActiveRoute(item.href)
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-gray-700 hover:text-primary-600 hover:bg-gray-50'
                    }`}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
            </div>

            {/* User Section (when authenticated) */}
            {isAuthenticated && (
              <div className="px-4 py-4 bg-white">
                <p className="px-2 py-2 text-xs font-semibold text-gray-400 uppercase tracking-wider">Account</p>
                <div className="flex items-center space-x-3 px-4 py-3 bg-gray-50 rounded-lg mb-3">
                  <ProfileImage
                    src={user?.avatar}
                    alt={user?.name || user?.email || 'User'}
                    name={user?.name}
                    email={user?.email}
                    userId={user?.id}
                    size="sm"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{user?.name || 'User'}</p>
                    <p className="text-xs text-gray-500 truncate">{user?.email}</p>
                    {roleLabel && (
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-primary-100 text-primary-800 rounded-full">
                        {roleLabel}
                      </span>
                    )}
                  </div>
                </div>
                <div className="space-y-1">
                  <Link
                    to={accountHome}
                    onClick={closeMobileMenu}
                    className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                  >
                    <svg className="w-5 h-5 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                    </svg>
                    {accountHomeLabel}
                  </Link>
                  {accountHome !== '/dashboard' && (
                    <Link
                      to="/dashboard"
                      onClick={closeMobileMenu}
                      className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                    >
                      <svg className="w-5 h-5 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7m-2 2v7a2 2 0 01-2 2H9a2 2 0 01-2-2v-7" />
                      </svg>
                      My Career Dashboard
                    </Link>
                  )}
                  {!role && (
                    <Link
                      to="/dashboard/profile"
                      onClick={closeMobileMenu}
                      className="flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-100 rounded-lg"
                    >
                      <svg className="w-5 h-5 mr-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      Profile Settings
                    </Link>
                  )}
                  <button
                    onClick={handleLogout}
                    className="flex items-center w-full px-4 py-3 text-sm text-error hover:bg-error/10 rounded-lg"
                  >
                    <svg className="w-5 h-5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                    Sign Out
                  </button>
                </div>
              </div>
            )}

            {/* Sign In button for non-authenticated users */}
            {!isAuthenticated && (
              <div className="px-4 py-4 bg-white">
                <Link
                  to="/login"
                  onClick={closeMobileMenu}
                  className="flex items-center justify-center w-full bg-primary-600 text-white px-4 py-3 rounded-lg text-sm font-medium hover:bg-primary-700"
                >
                  Sign In
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
